import { ChangeDetectorRef, Directive, HostBinding, Input } from '@angular/core';
import { IButtonState } from '@partners/ui';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Directive({
  standalone: true,
  selector: '[buttonState]',
  exportAs: 'btnState'
})
export class ButtonStateDirective {
  @Input()
  value: string | number;

  @Input()
  buttonState: any;

  @Input()
  @HostBinding('class.selected')
  set selected(value: boolean) {
    this._selected = value;

    if (value === true) {
      this.dirty = true;
      this.pristine = false;
    }
  }
  get selected(): boolean {
    return this._selected;
  }

  @Input()
  @HostBinding('class.pristine')
  set pristine(value: boolean) {
    this._pristine = value;

    if (value) {
      this._touched = false;
      this._dirty = false;
    }
    this.updateState();
  }
  get pristine(): boolean {
    return this._pristine;
  }

  @Input()
  @HostBinding('class.dirty')
  set dirty(value: boolean) {
    this._dirty = value;
    this._dirty && (this._pristine = false);
    this.updateState();
  }
  get dirty(): boolean {
    return this._dirty;
  }

  @Input()
  @HostBinding('class.touched')
  set touched(value: boolean) {
    this._touched = value;
    // this._touched; /* && (this._pristine = false);*/
    this.updateState();
  }
  get touched(): boolean {
    return this._touched;
  }

  /** global properties */
  /** streams the current state properties of this button */
  get context$(): Observable<IButtonState> {
    return this._state.asObservable();
  }

  get data(): any {
    return this.buttonState;
  }

  get context(): IButtonState {
    return {
      selected: this.selected,
      dirty: this.dirty,
      touched: this.touched,
      pristine: this.pristine
    } as IButtonState;
  }

  /** local state properties */
  private _dirty: boolean;
  private _touched: boolean;
  private _selected: boolean;
  private _pristine: boolean;

  /** a local state-snapshot emitter */
  private _state: BehaviorSubject<IButtonState>;

  /** subs manager */
  private _subscription: Subscription;

  constructor(protected changeDetector: ChangeDetectorRef) {
    this._subscription = new Subscription();

    /** update the button state on initialization to reflect latest state values */
    this._state = new BehaviorSubject<IButtonState>({
      touched: this.touched,
      pristine: this.pristine,
      selected: this.selected,
      dirty: this.dirty
    });

    this.changeDetector.markForCheck();
  }

  /**
   * house keeping
   */
  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._state.complete();
  }

  ngOnInit(): void {
    this.updateState({
      dirty: false,
      touched: false,
      selected: false,
      pristine: true
    });
    this.changeDetector.detectChanges();
  }

  /**
   * @description
   * updates the state properties of the button, then emit those values to any subscribers
   *
   * state is comprised of these properties: selected, dirty, pristine, touched - all boolean.
   * if no args are passed in, the updater will only emit the current state without updating
   *
   * @param state an anonymous object with the state properties { dirty: true... etc' }
   */
  updateState(state: Partial<IButtonState> = undefined) {
    if (state) {
      'selected' in state && (this._selected = state.selected);
      'touched' in state && (this._touched = state.touched);

      if ('dirty' in state) {
        this._dirty = state.dirty;
        this._pristine = !this._dirty;
      }

      if ('pristine' in state) {
        this._pristine = state.pristine;
        this._dirty = !this._pristine;
      }
    }
    this.triggerChange();
  }

  /**
   * @description
   * emits a stream of button's state properties (Partial<IButtonContext>)
   */
  private triggerChange() {
    /** create the new state snapshot */
    const newState = {
      ...{
        selected: this._selected,
        dirty: this._dirty,
        touched: this._touched,
        pristine: this._pristine
      }
    } as IButtonState;

    /** emit the new state to any consumers */
    this._state.next(newState);

    this.changeDetector.detectChanges();
  }
}
